import { useState } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useProducts, useFilterOptions } from '../api';
import { ProductGrid } from '../components/product/ProductGrid';
import { ProductFilters } from '../components/product/ProductFilters';
import { SEO, Breadcrumb } from '../components/SEO';
import { DEFAULT_PAGE_SIZE } from '@petmas/shared';

const categoryMeta: Record<string, { title: string; description: string; icon: string }> = {
  perros: {
    title: 'Productos para Perros',
    description: 'Alimentos, juguetes, accesorios y mas para tu mejor amigo',
    icon: '🐕',
  },
  gatos: {
    title: 'Productos para Gatos',
    description: 'Todo lo que necesita tu felino consentido',
    icon: '🐱',
  },
  prendas: {
    title: 'Prendas para Mascotas',
    description: 'Ropa y accesorios para que tu mascota luzca increible',
    icon: '👕',
  },
  placas: {
    title: 'Placas de Identificacion',
    description: 'Placas personalizadas para la seguridad de tu mascota',
    icon: '🏷️',
  },
  farmacia: {
    title: 'Farmacia Veterinaria',
    description: 'Medicamentos y productos de salud para mascotas',
    icon: '💊',
  },
  outlet: {
    title: 'Outlet',
    description: 'Las mejores ofertas en productos seleccionados',
    icon: '🏷️',
  },
};

const sortOptions = [
  { value: 'relevance', label: 'Relevancia' },
  { value: 'price-asc', label: 'Precio: menor a mayor' },
  { value: 'price-desc', label: 'Precio: mayor a menor' },
  { value: 'name-asc', label: 'Nombre: A-Z' },
  { value: 'newest', label: 'Mas recientes' },
];

// Default price ranges (fallback if API fails)
const defaultPriceRanges = [
  { value: '0-50', label: 'Hasta S/50' },
  { value: '50-100', label: 'S/50 - S/100' },
  { value: '100-200', label: 'S/100 - S/200' },
  { value: '200+', label: 'Mas de S/200' },
];

export function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [sortBy, setSortBy] = useState('relevance');
  const [selectedBrand, setSelectedBrand] = useState<string | undefined>();
  const [selectedPriceRange, setSelectedPriceRange] = useState<string | undefined>();
  const [selectedType, setSelectedType] = useState<string | undefined>();

  const page = parseInt(searchParams.get('page') ?? '1', 10);

  // Parse price range into min/max values
  const parsePriceRange = (range: string | undefined): { minPrice?: number; maxPrice?: number } => {
    if (!range) return {};
    if (range.endsWith('+')) {
      return { minPrice: parseInt(range.replace('+', ''), 10) };
    }
    const [min, max] = range.split('-').map(v => parseInt(v, 10));
    return { minPrice: min, maxPrice: max };
  };

  const { minPrice, maxPrice } = parsePriceRange(selectedPriceRange);

  // Fetch products
  const { data, isLoading } = useProducts({
    category,
    brand: selectedBrand,
    minPrice,
    maxPrice,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: sortBy !== 'relevance' ? sortBy : undefined,
  });

  // Fetch filter options for this category
  const { data: filterOptions, isLoading: isLoadingFilters } = useFilterOptions(category);

  const meta = category ? categoryMeta[category] : null;

  const handlePageChange = (newPage: number) => {
    setSearchParams({ page: String(newPage) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    setSelectedBrand(undefined);
    setSelectedPriceRange(undefined);
    setSelectedType(undefined);
  };

  // Build canonical URL
  const canonicalUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${location.pathname}`
    : undefined;

  // Build breadcrumb items
  const breadcrumbItems = [
    { name: 'Inicio', url: typeof window !== 'undefined' ? window.location.origin : '/' },
    { name: meta?.title ?? category ?? 'Productos', url: canonicalUrl ?? location.pathname },
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* SEO */}
      <SEO
        title={meta?.title ?? category}
        description={meta?.description}
        canonical={canonicalUrl}
      />

      {/* Header */}
      <div className="bg-white border-b">
        <div className="container py-8">
          {/* Breadcrumb */}
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <div className="flex items-center gap-4">
            {meta && (
              <span className="text-4xl">{meta.icon}</span>
            )}
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                {meta?.title ?? 'Productos'}
              </h1>
              {meta?.description && (
                <p className="mt-1 text-gray-600">{meta.description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar filters */}
          <aside className="lg:w-64 flex-shrink-0">
            <ProductFilters
              brands={filterOptions?.brands ?? []}
              priceRanges={filterOptions?.priceRanges ?? defaultPriceRanges}
              types={[]}
              selectedBrand={selectedBrand}
              selectedPriceRange={selectedPriceRange}
              selectedType={selectedType}
              onBrandChange={setSelectedBrand}
              onPriceRangeChange={setSelectedPriceRange}
              onTypeChange={setSelectedType}
              onClearAll={handleClearFilters}
              isLoading={isLoadingFilters}
            />
          </aside>

          {/* Main content */}
          <main className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-gray-600">
                {data ? `${data.total} productos encontrados` : 'Cargando...'}
              </p>
              <div className="flex items-center gap-2">
                <label htmlFor="sort" className="text-sm text-gray-600">
                  Ordenar por:
                </label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="input py-1.5 px-3 w-auto"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Products */}
            <ProductGrid
              products={data?.items ?? []}
              isLoading={isLoading}
              emptyMessage="No se encontraron productos en esta categoria"
            />

            {/* Pagination */}
            {data && data.total > data.pageSize && (
              <Pagination
                currentPage={data.page}
                totalPages={Math.ceil(data.total / data.pageSize)}
                onPageChange={handlePageChange}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  const pages: (number | 'ellipsis')[] = [];

  // Build page numbers with ellipsis
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - 1 && i <= currentPage + 1)
    ) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== 'ellipsis') {
      pages.push('ellipsis');
    }
  }

  return (
    <nav className="flex items-center justify-center gap-1 mt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Pagina anterior"
      >
        <ChevronLeftIcon className="w-5 h-5" />
      </button>

      {pages.map((page, index) =>
        page === 'ellipsis' ? (
          <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1 rounded ${
              currentPage === page
                ? 'bg-primary text-white'
                : 'hover:bg-gray-100'
            }`}
          >
            {page}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Pagina siguiente"
      >
        <ChevronRightIcon className="w-5 h-5" />
      </button>
    </nav>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}
